import React from 'react';
import Markdown from 'react-markdown';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Typography from '@mui/material/Typography/Typography';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

import { PageContainer } from '../../components/PageContainer';
import { HackathonBanner } from '../../components/Hackathon/HackathonBanner';
import { BrandedThemeProvider } from '../../components/Theme/AppThemeProvider';
import { EditHackathonData } from '../../components/Hackathon/HackathonEditor';
import { HackathonPayload } from '../../services/hackathon.service';
import { Branding, resolveBranding } from '../../services/branding.service';
import { formatDateRange, getHackathonStatus, ideaDeadlineEnd, ideasOpen } from '../../utils/hackathonStatus';
import { RulesCard } from './HackathonPage';

/**
 * The hackathon page as people will first see it, built from the create form alone (no server): the banner, the
 * description and how it works, with no ideas or people yet.
 */
export const HackathonPreview = ({
    data,
    organizationBranding,
    onBack,
    action
}: {
    data: EditHackathonData,
    // the linked organization's branding, which the hackathon's own fills in
    organizationBranding?: Branding,
    onBack: () => void,
    // the way forward from here, e.g. "Create hackathon"
    action: React.ReactNode
}) => {
    // just enough of a hackathon for the status and the "How it works" card
    const hackathon = {
        ...data,
        categoryMeta: data.categoryMeta.map((meta, index) => ({ ...meta, label: data.categories[index] })),
        startDate: data.startDate?.toDate() ?? null,
        endDate: data.endDate?.toDate() ?? null,
        ideaSubmissionDeadline: data.ideaSubmissionDeadline?.toDate() ?? null,
        votingPeriodOn: false,
        winners: [],
        ideasClosedAt: null,
        judges: [],
        // whoever creates it organizes it
        organizers: [{ username: 'You', email: 'you' }]
    } as unknown as HackathonPayload;
    const status = getHackathonStatus(hackathon);
    const deadlineOpen = !!hackathon.ideaSubmissionDeadline && ideasOpen(hackathon);
    const branding = resolveBranding(organizationBranding, data.branding);
    const branded = Object.values(data.branding).some(Boolean);

    return (
        <PageContainer variant="plain">
            <Stack spacing={3}>
                <Alert severity="info" icon={false} sx={{ alignItems: 'center' }} action={
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                        <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={onBack}>Back to editing</Button>
                        {action}
                    </Stack>
                }>
                    <b>Preview.</b> This is what people see when they open your hackathon. Ideas and people show up once it's live.
                    {branded && ' Your branding shows to participants on a Pro event.'}
                </Alert>

                <BrandedThemeProvider branding={branding}>
                    <Stack spacing={3}>
                        <HackathonBanner
                            name={data.name || 'Untitled Hackathon'}
                            organizationName={branding.displayName}
                            status={status}
                            dateRange={formatDateRange(hackathon.startDate, hackathon.endDate)}
                            deadline={deadlineOpen ? ideaDeadlineEnd(hackathon.ideaSubmissionDeadline)!.toDate() : null}
                            people={[]}
                            ideaCount={0}
                            teamPrompt={deadlineOpen || !hackathon.ideaSubmissionDeadline
                                ? { title: 'No ideas yet', subtitle: 'Be the first to pitch one', actionLabel: 'Pitch', onAction: () => undefined }
                                : null} />

                        <Box sx={{
                            display: 'grid',
                            gap: 3,
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
                            alignItems: 'start'
                        }}>
                            <Card>
                                <CardContent sx={{ p: { xs: 2, md: 3 } }} className="marked">
                                    {
                                        data.description
                                            ? <Markdown>{data.description}</Markdown>
                                            : <Typography color="text.secondary">No description yet.</Typography>
                                    }
                                </CardContent>
                            </Card>
                            <RulesCard hackathon={hackathon} isOrganizer={false} />
                        </Box>

                        <Stack spacing={2}>
                            <Typography variant="h3" component="h2">Ideas</Typography>
                            <Card sx={{ borderStyle: 'dashed' }}>
                                <CardContent sx={{ py: 5, textAlign: 'center' }}>
                                    <LightbulbOutlinedIcon color="action" sx={{ fontSize: 40, mx: 'auto' }} />
                                    <Typography variant="h5" sx={{ mt: 1 }}>No ideas yet</Typography>
                                    <Typography color="text.secondary">Participants pitch ideas and form teams here.</Typography>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Stack>
                </BrandedThemeProvider>
            </Stack>
        </PageContainer>
    );
};
